import React, { useState } from 'react'
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom'
import { Layout } from './components/Layout'
import { AdminDashboard } from './components/AdminDashboard'
import { authApi } from './services/adminApi'
import { Toaster } from 'react-hot-toast'

// 管理员登录组件
const AdminLoginPage: React.FC = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      console.log('Admin Login attempt:', { email, password })

      // 调用真正的管理员登录API
      const response = await authApi.login({ email, password })

      if (response.success) {
        // 保存token和用户信息
        const token = response.data.token
        const refreshToken = response.data.refreshToken

        console.log('Saving tokens:', {
          token: typeof token,
          tokenValue: token,
          refreshToken: typeof refreshToken,
          refreshValue: refreshToken
        })

        localStorage.setItem('token', typeof token === 'string' ? token : String(token))
        localStorage.setItem('refreshToken', typeof refreshToken === 'string' ? refreshToken : String(refreshToken))
        localStorage.setItem('user', JSON.stringify(response.data.user))
        localStorage.setItem('isAuthenticated', 'true')
        localStorage.setItem('userType', 'admin')

        console.log('Login successful:', response.data.user)
        console.log('Saved token:', localStorage.getItem('token'))

        // 触发登录成功事件
        window.dispatchEvent(new CustomEvent('loginSuccess'))

        // 添加延迟确保状态更新后再导航
        setTimeout(() => {
          navigate('/dashboard', { replace: true })
        }, 100)
      } else {
        console.error('Login failed:', response.error?.message)
        alert('登录失败: ' + (response.error?.message || '未知错误'))
      }

    } catch (error: any) {
      console.error('Login error:', error)
      alert('登录失败: ' + (error.message || '网络连接错误'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">TalkNect</h1>
          <p className="text-gray-600">管理端 - 全球语言学习平台</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              管理员邮箱
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
              placeholder="请输入管理员邮箱"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              密码
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
              placeholder="••••••••••••"
              required
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-purple-600 hover:bg-purple-700 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? '登录中...' : '管理员登录'}
          </button>

          <div className="text-center">
            <p className="text-sm text-gray-600">
              <a href="/admin/login" className="text-purple-600 hover:text-purple-500 font-medium">
                返回管理员登录
              </a>
            </p>
          </div>
        </form>
      </div>
    </div>
  )
}

// 管理员主应用组件
function AdminApp() {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    const auth = localStorage.getItem('isAuthenticated')
    const userType = localStorage.getItem('userType')
    return auth === 'true' && userType === 'admin'
  })

  // 检查认证状态
  React.useEffect(() => {
    const checkAuth = () => {
      const auth = localStorage.getItem('isAuthenticated')
      const userType = localStorage.getItem('userType')
      setIsAuthenticated(auth === 'true' && userType === 'admin')
    }

    // 监听localStorage变化
    const handleStorageChange = () => {
      checkAuth()
    }

    // 监听自定义事件
    const handleLoginSuccess = () => {
      checkAuth()
    }

    window.addEventListener('storage', handleStorageChange)
    window.addEventListener('loginSuccess', handleLoginSuccess)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('loginSuccess', handleLoginSuccess)
    }
  }, [])

  return (
    <>
      <Routes>
        {!isAuthenticated ? (
          <>
            <Route path="/login" element={<AdminLoginPage />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </>
        ) : (
          <>
            <Route path="/login" element={<Navigate to="/dashboard" replace />} />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<AdminDashboard />} />
          </>
        )}
      </Routes>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: '#4ade80',
              secondary: '#fff',
            },
          },
          error: {
            duration: 5000,
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
    </>
  )
}

export default AdminApp
